import {
  OrderLine,
  OrderLineType,
  QueryMode,
  Refund,
  RefundCustomerSearchQuery,
  OrderStatus,
  OrderLineStatus,
  RefundStatus,
  SortOrder,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Avatar,
  Button,
  Combobox,
  Group,
  Loader,
  NumberInput,
  Pagination,
  Select,
  Stack,
  Text,
  TextInput,
  useCombobox,
  Badge,
  Divider,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { isInRange, isNotEmpty, matches, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  refundCustomerOrdersDocument,
  refundCustomerSearchDocument,
  refundsDocument,
} from '../graphql/queries';
import { upsertRefundDocument } from '../graphql/upsert-refund';
import { useState } from 'react';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { usePagination } from '@mantine/hooks';
import { IconInfoCircle, IconQuestionMark } from '@tabler/icons-react';
import { isValid } from 'iban';

const lineStatusColors = {
  [OrderLineStatus.Created]: 'gray',
  [OrderLineStatus.Packed]: 'blue',
  [OrderLineStatus.Confirmed]: 'green',
  [OrderLineStatus.Reviewing]: 'orange',
  [OrderLineStatus.Returned]: 'red',
};

const lineStatusTranslations = {
  [OrderLineStatus.Created]: 'Objednané',
  [OrderLineStatus.Packed]: 'Zabalené',
  [OrderLineStatus.Confirmed]: 'Potvrdené',
  [OrderLineStatus.Reviewing]: 'V riešení',
  [OrderLineStatus.Returned]: 'Vrátené',
};

const orderStatusTranslations = {
  [OrderStatus.Created]: 'Vytvorená',
  [OrderStatus.Confirmed]: 'Potvrdená',
  [OrderStatus.Processed]: 'Spracovaná',
  [OrderStatus.Shipped]: 'Odoslaná',
  [OrderStatus.Delivered]: 'Doručená',
  [OrderStatus.Cancelled]: 'Zrušená',
  [OrderStatus.Reviewing]: 'V riešení',
  [OrderStatus.Returned]: 'Vrátená',
  [OrderStatus.Internal]: 'Interná',
  [OrderStatus.Reserved]: 'Rezervovaná',
};

const orderStatusColors = {
  [OrderStatus.Created]: 'gray',
  [OrderStatus.Confirmed]: 'blue',
  [OrderStatus.Processed]: 'cyan',
  [OrderStatus.Shipped]: 'teal',
  [OrderStatus.Delivered]: 'green',
  [OrderStatus.Cancelled]: 'red',
  [OrderStatus.Reviewing]: 'orange',
  [OrderStatus.Returned]: 'gray',
  [OrderStatus.Internal]: 'dark',
  [OrderStatus.Reserved]: 'violet',
};

type RefundFormValues = Pick<
  Refund,
  'id' | 'reason' | 'note' | 'amount' | 'iban' | 'customerId' | 'status'
> & {
  lines?:
    | Pick<OrderLine, 'id' | 'type' | 'code' | 'name' | 'status' | 'price' | 'description'>[]
    | null;
};

export const RefundUpsertModal = ({ refund }: { refund?: RefundFormValues }) => {
  const form = useForm<RefundFormValues>({
    initialValues: {
      id: refund?.id ?? '',
      reason: refund?.reason ?? '',
      note: refund?.note,
      amount: refund?.amount ?? 0,
      iban: refund?.iban,
      customerId: refund?.customerId ?? '',
      status: refund?.status ?? RefundStatus.Created,
      lines: refund?.lines,
    },
    validate: {
      reason: isNotEmpty('Zadajte dôvod reklamácie'),
      amount: isInRange({ min: 1 }, 'Suma musí byť väčšia ako 0'),
      iban: (value) =>
        value
          ? !isValid(value)
            ? 'Zadajte platný IBAN'
            : matches(/^[A-Z]{2}\d{2}(?:\s\d{4}){4,7}$/, 'Zadajte IBAN v správnom tvare')(value)
          : null,
      customerId: isNotEmpty('Vyberte zákazníka'),
      status: isNotEmpty('Vyberte status refundácie'),
    },
  });

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setCustomerSearch('');
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const [customerSearch, setCustomerSearch] = useState('');

  const hasSearch = customerSearch.length >= 2;
  const hasCustomerId = form.values.customerId.length > 0;
  const tokens = customerSearch.trim().split(/\s+/).filter(Boolean);

  const where = hasSearch
    ? {
        AND: tokens.map((token) => ({
          name: { contains: token, mode: QueryMode.Insensitive },
        })),
      }
    : hasCustomerId
      ? { id: { equals: form.values.customerId } }
      : {};

  const { data: customers, loading: customersLoading } = useQuery(refundCustomerSearchDocument, {
    variables: { where, orderBy: { updatedAt: SortOrder.Desc }, take: 10 },
    skip: !hasSearch && !hasCustomerId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ customers }) => {
      if (!hasCustomerId) return;

      const customer = customers.find((c) => c.id === form.values.customerId);

      setSelectedCustomer(customer);
    },
  });

  const [selectedCustomer, setSelectedCustomer] =
    useState<RefundCustomerSearchQuery['customers'][number]>();

  const ordersPagination = usePagination({ total: selectedCustomer?._count.orders ?? 0 });

  const { data: orders, loading: ordersLoading } = useQuery(refundCustomerOrdersDocument, {
    variables: {
      where: {
        customer: { is: { id: { equals: selectedCustomer?.id } } },
      },
      skip: ordersPagination.active - 1,
      take: 1,
      orderBy: { createdAt: SortOrder.Desc },
    },
    skip: !selectedCustomer,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const paginatedOrder = orders?.orders.at(0);

  const orderLines =
    orders?.orders
      .flatMap((order) => order.lines ?? [])
      .sort((a, b) => (b.type === 'PRODUCT' ? 1 : 0) - (a.type === 'PRODUCT' ? 1 : 0)) ?? [];

  const mergedOrderLines = [
    ...(form.values.lines ?? []).filter(
      (selected) => !orderLines.some((line) => line.id === selected.id)
    ),
    ...orderLines,
  ];

  const [upsertRefund, { loading: upsertLoading }] = useMutation(upsertRefundDocument, {
    refetchQueries: [refundsDocument],
  });

  const handleSubmit = async (values: RefundFormValues) => {
    await upsertRefund({
      variables: {
        where: { id: values.id ?? '' },
        create: {
          reason: values.reason,
          note: values.note,
          amount: values.amount,
          iban: values.iban,
          status: values.status,
          customer: {
            connect: { id: values.customerId },
          },
          lines: { connect: values.lines?.map((line) => ({ id: line.id })) },
        },
        update: {
          reason: { set: values.reason },
          note: { set: values.note ?? null },
          amount: { set: values.amount },
          iban: { set: values.iban ?? null },
          status: { set: values.status },
          customer: {
            connect: { id: values.customerId },
          },
          lines: { set: values.lines?.map((line) => ({ id: line.id })) },
        },
      },
    });

    modals.closeAll();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        {form.errors.customerId && (
          <Alert color="red" icon={<IconInfoCircle size={16} />} title="POZOR!">
            {form.errors.customerId}
          </Alert>
        )}
        <Combobox
          store={combobox}
          position="bottom-start"
          withArrow
          withinPortal={false}
          onOptionSubmit={(customerId) => {
            form.setFieldValue('customerId', customerId);
            setSelectedCustomer(customers?.customers.find((c) => c.id === customerId));
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target withAriaAttributes={false}>
            <Group justify="space-between">
              <Group>
                <Avatar src={selectedCustomer?.avatar} />
                <Stack gap={0}>
                  <Text inherit>{selectedCustomer?.name ?? 'Zvoľ zákazníka'}</Text>
                  <Text inherit size="xs" c="dimmed">
                    {dayjs(selectedCustomer?.createdAt).format('DD.MM.YYYY HH:mm')}
                  </Text>
                  <Text inherit size="xs" c="dimmed">
                    {dayjs(selectedCustomer?.updatedAt).format('DD.MM.YYYY HH:mm')}
                  </Text>
                </Stack>
              </Group>
              <Stack gap={0}>
                <Text inherit>{selectedCustomer?._count.orders ?? '-'} objednávok</Text>
                <Text inherit>{selectedCustomer?._count.refunds ?? '-'} reklamácií</Text>
              </Stack>
              {selectedCustomer && !selectedCustomer.active && (
                <Stack gap={0}>
                  <Text inherit c="red">
                    Zákazník je zablokovaný
                  </Text>
                  <Text inherit size="xs" c="dimmed">
                    {selectedCustomer?.reason ?? 'Neznámy dôvod'}
                  </Text>
                </Stack>
              )}
              <Button onClick={() => combobox.openDropdown()}>Zvoliť zákazníka</Button>
            </Group>
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Search
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.currentTarget.value)}
              placeholder="Hľadať zákazníka"
              rightSection={customersLoading ? <Loader size={16} /> : null}
            />
            <Combobox.Options>
              {customers?.customers.map((customer) => (
                <Combobox.Option value={customer.id} key={customer.id}>
                  <Group justify="space-between">
                    <Group>
                      <Avatar src={customer.avatar} size={16} />
                      <Stack gap={0}>
                        <Text inherit>{customer.name}</Text>
                        <Text inherit size="xs" c="dimmed">
                          {dayjs(customer.createdAt).format('DD.MM.YYYY HH:mm')}
                        </Text>
                        <Text inherit size="xs" c="dimmed">
                          {dayjs(customer.updatedAt).format('DD.MM.YYYY HH:mm')}
                        </Text>
                      </Stack>
                    </Group>
                    {!customer.active && (
                      <Stack gap={0}>
                        <Text inherit c="red">
                          Zákazník je zablokovaný
                        </Text>
                        <Text inherit size="xs" c="dimmed">
                          {customer?.reason ?? 'Neznámy dôvod'}
                        </Text>
                      </Stack>
                    )}
                    <Stack gap={0}>
                      <Text inherit>{customer._count.orders ?? '-'} objednávok</Text>
                      <Text inherit>{customer._count.refunds ?? '-'} reklamácií</Text>
                    </Stack>
                  </Group>
                </Combobox.Option>
              ))}
              {customers?.customers.length === 0 && (
                <Combobox.Empty>Nenašiel sa žiaden zákazník</Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <Group grow>
          <TextInput
            label="Dôvod"
            placeholder="Zadaj dôvod reklamácie"
            required
            rightSection={
              <ActionIcon
                variant="subtle"
                onClick={() => form.setFieldValue('reason', 'chyba z nasej strany')}
              >
                <IconQuestionMark size={16} />
              </ActionIcon>
            }
            {...form.getInputProps('reason')}
          />

          <TextInput
            label="Poznámka"
            placeholder="Zadaj internú poznámku k reklamácií (nepovinné)"
            {...form.getInputProps('note')}
          />
        </Group>

        {form.values.amount > 0 &&
          (form.values.lines?.reduce((sum, l) => sum + l.price, 0) ?? 0) !== form.values.amount && (
            <Alert color="orange" icon={<IconInfoCircle size={16} />} title="POZOR!">
              Skontroluj sumu - je iná ako suma vybraných položiek.
            </Alert>
          )}

        <Group grow>
          <TextInput
            label="IBAN"
            placeholder="Zadaj IBAN pre vrátenie peňazí"
            {...form.getInputProps('iban')}
          />
          <NumberInput
            label="Suma"
            placeholder="Zadaj sumu na vrátenie (opíš po označení produktov)"
            suffix=" €"
            required
            {...form.getInputProps('amount')}
          />
        </Group>
        {refund?.status && (
          <Select
            label="Status"
            placeholder="Vyberte status refundácie"
            data={Object.values(RefundStatus)}
            required
            {...form.getInputProps('status')}
          />
        )}

        <Divider />

        {selectedCustomer && (
          <Group justify="space-between">
            <Text fw={600}>Vybrané položky: {form.values.lines?.length ?? 0}</Text>
            <Text fw={600}>
              Suma na vrátenie: {form.values.lines?.reduce((sum, l) => sum + l.price, 0) ?? 0} €
            </Text>
          </Group>
        )}

        {selectedCustomer && paginatedOrder && (
          <Group justify="space-between">
            <Stack gap="xs">
              <Text>{dayjs(paginatedOrder.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
              <Text c="dimmed">{dayjs(paginatedOrder.updatedAt).format('DD.MM.YYYY HH:mm')}</Text>
            </Stack>
            <Stack gap="xs" align="center">
              <Text fw={600} size="lg">
                Objednávka č. {paginatedOrder.package} - {paginatedOrder.total} €
              </Text>
              <Badge color={orderStatusColors[paginatedOrder.status]}>
                {orderStatusTranslations[paginatedOrder.status]}
              </Badge>
            </Stack>
            <Stack gap="xs">
              <Text>{paginatedOrder.shippingMethod?.name}</Text>
              <Text>{paginatedOrder.paymentMethod?.name}</Text>
            </Stack>
          </Group>
        )}

        {selectedCustomer && (
          <DataTable
            noHeader
            highlightOnHover
            striped
            withTableBorder
            height={200}
            fetching={ordersLoading}
            records={mergedOrderLines}
            columns={[
              {
                accessor: 'code',
                render: (line) => (
                  <Group gap="xs">
                    <Text inherit>{line.type === OrderLineType.Product ? line.code : '-'}</Text>
                    {!orderLines.some((l) => l.id === line.id) && (
                      <Tooltip label="Z inej objednávky">
                        <Badge size="sm" c="gray" variant="outline">
                          ⓘ
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                ),
              },
              {
                accessor: 'name',
                render: (line) => (
                  <Tooltip multiline label={line.description}>
                    <Text inherit>{line.name}</Text>
                  </Tooltip>
                ),
              },
              { accessor: 'price', render: (line) => `${line.price} €` },
              {
                accessor: 'status',
                render: ({ status }) => (
                  <Badge color={lineStatusColors[status]}>{lineStatusTranslations[status]}</Badge>
                ),
              },
            ]}
            selectedRecords={form.values.lines ?? []}
            onSelectedRecordsChange={(selected) => form.setFieldValue('lines', selected)}
            selectionTrigger="cell"
          />
        )}

        {selectedCustomer && (
          <Pagination.Root
            total={selectedCustomer._count.orders ?? 0}
            value={ordersPagination.active}
            onChange={ordersPagination.setPage}
          >
            <Group justify="space-between">
              <Pagination.Previous />
              <Group>
                <Pagination.Items />
              </Group>
              <Pagination.Next />
            </Group>
          </Pagination.Root>
        )}

        <Button type="submit" loading={upsertLoading}>
          {refund ? 'Upraviť' : 'Vytvoriť'}
        </Button>
      </Stack>
    </form>
  );
};
